/**
 * Mal kabul (irsaliye) birim dönüşümü — saf mantık, UI'dan bağımsız.
 *
 * SORUN: Hammaddeler reçetede ve stokta küçük birimle (gr/ml) tutulur, ama
 * tedarikçi faturayı büyük birimle (kg/lt) keser. Kullanıcı "1 kg = 123 ₺"
 * girmek ister; bu, gram cinsinden stoğa "1000 gr, gram-başı maliyet"
 * olarak işlenmelidir. Backend `quantity_received` ve `unit_price_minor`'ı
 * STOK birimi cinsinden bekler.
 *
 * Bu modül o dönüşümü tek yerde, test edilebilir tutar.
 */

export interface PurchaseUnitOption {
  value: string;
  label: string;
  /** 1 alış birimi = factor × stok birimi (örn. 1 kg = 1000 gr). */
  factor: number;
}

/**
 * Verilen STOK birimi için kullanıcının seçebileceği ALIŞ birimleri.
 *
 * gr/ml gibi küçük birimlerde büyük karşılığı (kg/lt) da sunulur. Zaten
 * büyük olan birimlerde (kg, lt, adet, porsiyon) tek seçenek = kendisi,
 * dönüşüm olmaz. Liste her zaman en az bir öğe içerir; dönüşümlü
 * birimlerde büyük birim SONDADIR (varsayılan seçim onu önerir).
 */
export function purchaseUnitsFor(stockUnit: string): PurchaseUnitOption[] {
  switch (stockUnit) {
    case "gr":
      return [
        { value: "gr", label: "gr", factor: 1 },
        { value: "kg", label: "kg", factor: 1000 },
      ];
    case "ml":
      return [
        { value: "ml", label: "ml", factor: 1 },
        { value: "lt", label: "lt", factor: 1000 },
      ];
    default:
      return [{ value: stockUnit, label: stockUnit, factor: 1 }];
  }
}

/** Bir alış biriminin stok birimine oranı; bilinmeyen birimde 1 (güvenli). */
export function factorFor(stockUnit: string, purchaseUnit: string): number {
  return purchaseUnitsFor(stockUnit).find((o) => o.value === purchaseUnit)?.factor ?? 1;
}

// ============================================================
// Ölçü ailesi — yön-bağımsız birim dönüşümü (reçete girişi için)
// ============================================================
//
// purchaseUnitsFor yalnızca "küçük stok birimi → büyük alış birimi" yönünü
// kapsar (gr→kg). Reçetede ters de gerekir: stok birimi kg iken kullanıcı
// gram girmek ister. Bunun için her birimi tek bir BAZ birime oranlayan
// aile tablosu kullanırız; dönüşüm böylece yönden bağımsız olur.
//
// baseFactor: 1 <birim> kaç baz birim eder. Aynı ailedeki iki birim arası
// oran = from.baseFactor / to.baseFactor (örn. gr→kg = 1/1000).

interface UnitDef {
  value: string;
  label: string;
  /** 1 <value> = baseFactor × ailenin baz birimi. */
  baseFactor: number;
}

/** Ölçü aileleri. Her ailede birimler küçükten büyüğe sıralı. */
const UNIT_FAMILIES: UnitDef[][] = [
  // Kütle — baz: gr
  [
    { value: "gr", label: "gr", baseFactor: 1 },
    { value: "kg", label: "kg", baseFactor: 1000 },
  ],
  // Hacim — baz: ml
  [
    { value: "ml", label: "ml", baseFactor: 1 },
    { value: "lt", label: "lt", baseFactor: 1000 },
  ],
];

function familyOf(unit: string): UnitDef[] | undefined {
  return UNIT_FAMILIES.find((fam) => fam.some((u) => u.value === unit));
}

/**
 * Reçeteye miktar girerken kullanıcının seçebileceği birimler.
 *
 * Stok birimi bir ölçü ailesindeyse (gr/kg veya ml/lt) ailenin TÜM
 * birimlerini döndürür — yön fark etmez: stok kg olsa da gr seçilebilir,
 * stok gr olsa da kg seçilebilir. Aile dışı birimlerde (adet, porsiyon)
 * tek seçenek kendisidir. Liste her zaman ≥1 öğe içerir.
 */
export function recipeUnitsFor(stockUnit: string): { value: string; label: string }[] {
  const fam = familyOf(stockUnit);
  if (!fam) return [{ value: stockUnit, label: stockUnit }];
  return fam.map((u) => ({ value: u.value, label: u.label }));
}

/**
 * 1 <inputUnit> kaç <stockUnit> eder. Aynı ailedeyseler gerçek oran,
 * değilseler 1 (güvenli — birim aynı varsayılır). Reçete miktarını stok
 * birimine çevirmek için: quantityInStockUnit = inputQty × conversionFactor.
 *
 * Örnek: stok kg, kullanıcı gr girdi → 1 gr = 0.001 kg → factor 0.001.
 *        250 gr × 0.001 = 0.250 kg.
 */
export function conversionFactor(stockUnit: string, inputUnit: string): number {
  if (stockUnit === inputUnit) return 1;
  const fam = familyOf(stockUnit);
  if (!fam) return 1;
  const from = fam.find((u) => u.value === inputUnit);
  const to = fam.find((u) => u.value === stockUnit);
  if (!from || !to) return 1;
  return from.baseFactor / to.baseFactor;
}

/**
 * Aynı ailede stok birimine göre VARSAYILAN giriş birimini önerir:
 * büyük stok biriminde (kg/lt) küçük birim (gr/ml) — reçeteler çoğunlukla
 * küçük miktar kullanır; küçük stok biriminde kendisini önerir.
 */
export function defaultRecipeInputUnit(stockUnit: string): string {
  const fam = familyOf(stockUnit);
  if (!fam) return stockUnit;
  // Stok ailenin en büyük birimiyse (son eleman) en küçüğü öner.
  const isLargest = fam[fam.length - 1].value === stockUnit;
  return isLargest ? fam[0].value : stockUnit;
}

export interface DraftLineInput {
  /** Stok/reçete birimi (örn. "gr"). */
  stockUnit: string;
  /** Kullanıcının girdiği alış birimi (örn. "kg"). */
  purchaseUnit: string;
  /** Miktar — alış birimi cinsinden (örn. 1 = 1 kg). */
  quantity: number;
  /** Birim fiyat — alış birimi başına KURUŞ (örn. 12300 = 123 ₺/kg). */
  purchasePriceMinor: number;
}

export interface LinePayload {
  /** Stok birimi cinsinden miktar (örn. 1000 gr). */
  quantityReceived: number;
  /** Stok birimi başına kuruş (örn. 12 kuruş/gr). */
  unitPriceMinor: number;
  /** Satır toplamı — kuruş, kesin (örn. 12300 = 123 ₺). */
  lineTotalMinor: number;
}

/**
 * Bir form satırını backend payload'ına çevir.
 *
 * Strateji (bilinçli karar): önce satır TOPLAMINI kuruş olarak kesin
 * hesapla, sonra stok birimi başına fiyatı toplamdan GERİ türet. Böylece
 * "miktar × birim = toplam" her zaman tutar; küçük stok birimlerinde
 * (gr/ml) kaçınılmaz olan kesirli kuruş yuvarlaması yalnızca AVCO maliyet
 * snapshot'ında ~%0.x düzeyinde kalır, görünen toplamı bozmaz.
 *
 * Örnek: 1 kg @ 123 ₺/kg, stok gr
 *   quantityReceived = 1000, lineTotalMinor = 12300,
 *   unitPriceMinor   = round(12300 / 1000) = 12 (kuruş/gr)
 */
export function lineToPayload(line: DraftLineInput): LinePayload {
  const factor = factorFor(line.stockUnit, line.purchaseUnit);
  const quantityReceived = line.quantity * factor;
  const lineTotalMinor = Math.round(line.quantity * line.purchasePriceMinor);
  const unitPriceMinor =
    quantityReceived > 0 ? Math.round(lineTotalMinor / quantityReceived) : 0;
  return { quantityReceived, unitPriceMinor, lineTotalMinor };
}
